import { browserLocalStorage, createLocalStorageStore, type StorageAccess } from '@/lib/local-storage-store';

import { FeaturePreviewId, isAvailableFeaturePreviewId, type FeaturePreviewIdValue } from './feature-preview-catalog';

/**
 * `feature-preview-program:preview-opt-ins-stay-client-local`, `feature-preview-program:new-design-defaults-on` — one person's effective previews, per browser and origin.
 *
 * Missing, unavailable, or invalid storage enables the available New design
 * default without writing storage. A valid saved list is authoritative, even
 * when empty (the person turned the preview off). Unknown and retired ids
 * are dropped on decode (`feature-preview-program:closed-catalog-drops-retired-previews`), including from the defaults.
 */
export const FEATURE_PREVIEW_STORAGE_KEY = 'crewstack:feature-previews';

/** Query parameter that turns a preview on from a link (`feature-preview-program:preview-link-only-enables`). Comma-separated or repeated. */
export const FEATURE_PREVIEW_LINK_PARAMETER = 'preview';

const FEATURE_PREVIEW_STORAGE_VERSION = 1;

export type FeaturePreviewOptIns = ReadonlySet<FeaturePreviewIdValue>;

const DEFAULT_OPT_INS: FeaturePreviewOptIns = new Set(
	[FeaturePreviewId.WorkspaceRedesign].filter(isAvailableFeaturePreviewId),
);

export function decodeFeaturePreviewOptIns(raw: string | null): FeaturePreviewOptIns {
	if (raw === null) return DEFAULT_OPT_INS;
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return DEFAULT_OPT_INS;
	}
	if (typeof parsed !== 'object' || parsed === null) return DEFAULT_OPT_INS;
	const record = parsed as { version?: unknown; enabled?: unknown };
	if (record.version !== FEATURE_PREVIEW_STORAGE_VERSION) return DEFAULT_OPT_INS;
	if (!Array.isArray(record.enabled)) return DEFAULT_OPT_INS;
	const enabled = new Set<FeaturePreviewIdValue>();
	for (const value of record.enabled) {
		if (isAvailableFeaturePreviewId(value)) enabled.add(value);
	}
	return enabled;
}

export function encodeFeaturePreviewOptIns(optIns: Iterable<FeaturePreviewIdValue>): string {
	return JSON.stringify({ version: FEATURE_PREVIEW_STORAGE_VERSION, enabled: [...optIns].sort() });
}

/** The available preview ids a link names; unknown names are ignored. */
export function parseFeaturePreviewLink(search: string): readonly FeaturePreviewIdValue[] {
	const ids = new Set<FeaturePreviewIdValue>();
	for (const value of new URLSearchParams(search).getAll(FEATURE_PREVIEW_LINK_PARAMETER)) {
		for (const name of value.split(',')) {
			const trimmed = name.trim();
			if (isAvailableFeaturePreviewId(trimmed)) ids.add(trimmed);
		}
	}
	return [...ids];
}

export type FeaturePreviewStore = {
	readonly subscribe: (listener: () => void) => () => void;
	/** Referentially stable while the stored value is unchanged (`useSyncExternalStore` contract). */
	readonly getSnapshot: () => FeaturePreviewOptIns;
	readonly setOptedIn: (id: FeaturePreviewIdValue, optedIn: boolean) => void;
	/** Applies a link's `?preview=` opt-ins (`feature-preview-program:preview-link-only-enables`). Only ever turns previews on. */
	readonly applyLink: (search: string) => void;
};

export function createFeaturePreviewStore(storageAccess: StorageAccess): FeaturePreviewStore {
	const store = createLocalStorageStore({
		key: FEATURE_PREVIEW_STORAGE_KEY,
		decode: decodeFeaturePreviewOptIns,
		encode: encodeFeaturePreviewOptIns,
		storageAccess,
	});
	return {
		subscribe: store.subscribe,
		getSnapshot: store.getSnapshot,
		setOptedIn(id, optedIn) {
			const current = store.getSnapshot();
			if (current.has(id) === optedIn) return;
			const next = new Set(current);
			if (optedIn) next.add(id);
			else next.delete(id);
			store.write(next);
		},
		applyLink(search) {
			const linked = parseFeaturePreviewLink(search);
			const current = store.getSnapshot();
			if (linked.every((id) => current.has(id))) return;
			store.write(new Set([...current, ...linked]));
		},
	};
}

export const featurePreviewStore: FeaturePreviewStore = createFeaturePreviewStore(browserLocalStorage);
